import type { Architecture, Channel, Platform, ReleasePolicy, ReleasePolicyInput } from "@player115/contracts";
import { ExternalLink, Plus, RefreshCw, Save, ShieldAlert } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { api, demoMode } from "../api";
import { PageState, Status } from "../components/Status";
import "./ReleasesPage.css";

const adminEnvironment = demoMode
  ? "demo"
  : window.location.hostname.startsWith("staging-")
    ? "staging"
    : window.location.hostname === "admin.5bo.app"
      ? "production"
      : "local";

const blankRelease: ReleasePolicyInput = {
  platform: "macos",
  architecture: "aarch64",
  channel: "stable",
  latestVersion: "0.1.0",
  minSupportedVersion: "0.1.0",
  forceUpgrade: false,
  enforceAt: null,
  title: "115影音新版可用",
  releaseNotes: [],
  downloadPageUrl: "",
  backupDownloadPageUrl: null,
  accessCode: null,
  sha256: null,
  enabled: false,
};

function toInput(release: ReleasePolicy): ReleasePolicyInput {
  const { id: _id, createdAt: _createdAt, updatedAt: _updatedAt, ...input } = release;
  return input;
}

function platformName(platform: Platform) {
  return platform === "macos" ? "macOS" : platform === "windows" ? "Windows" : platform === "android-tv" ? "Android TV" : platform === "android-mobile" ? "Android Mobile" : "Linux";
}

function localDateTimeValue(value: string | null) {
  if (!value) return "";
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

export function ReleasesPage() {
  const environmentLabel = adminEnvironment === "production" ? "正式环境：下发给稳定版客户端" : adminEnvironment === "staging" ? "测试环境：仅下发给测试版客户端" : adminEnvironment === "demo" ? "演示数据" : "本地环境";
  const [releases, setReleases] = useState<ReleasePolicy[]>([]);
  const [draft, setDraft] = useState<ReleasePolicyInput>(blankRelease);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const load = async () => {
    try {
      const result = await api.releases();
      setReleases(result.releases);
      if (result.releases[0] && !selectedId) {
        setSelectedId(result.releases[0].id);
        setDraft(toInput(result.releases[0]));
        setNotes(result.releases[0].releaseNotes.join("\n"));
      }
    } catch (reason) { setError(reason instanceof Error ? reason.message : "版本策略读取失败"); }
  };
  useEffect(() => { void load(); }, []);
  const selected = useMemo(() => releases.find((release) => release.id === selectedId), [releases, selectedId]);
  const update = <K extends keyof ReleasePolicyInput>(key: K, value: ReleasePolicyInput[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setMessage("");
    if (key === "forceUpgrade") setConfirmed(false);
  };
  const choose = (release: ReleasePolicy) => {
    setSelectedId(release.id);
    setDraft(toInput(release));
    setNotes(release.releaseNotes.join("\n"));
    setConfirmed(false);
    setMessage("");
  };
  const createNew = () => {
    setSelectedId(null);
    setDraft(blankRelease);
    setNotes("");
    setConfirmed(false);
    setMessage("");
  };
  const save = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const input = { ...draft, releaseNotes: notes.split(/\r?\n/).map((line) => line.trim()).filter(Boolean) };
      const result = await api.saveRelease(input);
      setReleases((current) => [...current.filter((item) => item.id !== result.release.id && !(item.platform === result.release.platform && item.architecture === result.release.architecture && item.channel === result.release.channel)), result.release].sort((a, b) => `${a.platform}${a.architecture}${a.channel}`.localeCompare(`${b.platform}${b.architecture}${b.channel}`)));
      setSelectedId(result.release.id);
      setDraft(toInput(result.release));
      setMessage("版本策略已保存并写入审计日志");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "保存失败"); }
    finally { setBusy(false); }
  };
  return <section className="page releases-page">
    <header className="page-head"><div><h1>版本发布</h1><p>第三方网盘入口与客户端兼容范围</p></div><div className="page-head-actions"><Status tone={adminEnvironment === "production" ? "danger" : "warning"}>{environmentLabel}</Status><button className="primary-button" type="button" onClick={createNew}><Plus size={17} />新建策略</button></div></header>
    <div className="release-targets">
      <header className="release-targets-head"><div><strong>发布目标</strong><small>选择要编辑的平台版本策略</small></div><span>{releases.length} 个目标</span></header>
      {releases.length ? <div className="release-target-list" aria-label="版本发布目标">
        {releases.map((release) => <button className={release.id === selectedId ? "is-selected" : ""} aria-pressed={release.id === selectedId} type="button" key={release.id} onClick={() => choose(release)}><span className="release-target-copy"><strong>{platformName(release.platform)} · {release.architecture}</strong><small>{release.channel === "stable" ? "稳定版" : "测试版"} · v{release.latestVersion}</small></span>{release.enabled ? <Status tone={release.forceUpgrade ? "danger" : "success"}>{release.forceUpgrade ? "强升" : "生效"}</Status> : <Status tone="muted">草稿</Status>}</button>)}
      </div> : <PageState title="暂无版本策略" detail="创建第一个平台发布策略。" />}
    </div>
    <form className="release-editor" onSubmit={(event) => { event.preventDefault(); void save(); }}>
        <header><div><h2>{selected ? `编辑 v${selected.latestVersion}` : "新建版本策略"}</h2><p>{draft.platform} / {draft.architecture} / {draft.channel}</p></div>{draft.enabled ? <Status tone="success">已启用</Status> : <Status tone="muted">未启用</Status>}</header>
        <section className="form-section"><h3>发布目标</h3><div className="form-grid three">
          <label><span>平台</span><select value={draft.platform} onChange={(event) => update("platform", event.target.value as Platform)}><option value="macos">macOS</option><option value="windows">Windows</option><option value="linux">Linux</option><option value="android-tv">Android TV</option><option value="android-mobile">Android Mobile</option></select></label>
          <label><span>架构</span><select value={draft.architecture} onChange={(event) => update("architecture", event.target.value as Architecture)}><option value="aarch64">{draft.platform === "macos" ? "ARM64（Apple 芯片）" : "ARM64"}</option><option value="armv7">ARMv7（32 位）</option><option value="x86_64">x86_64</option></select></label>
          <label><span>渠道</span><select value={draft.channel} onChange={(event) => update("channel", event.target.value as Channel)}><option value="stable">稳定版</option><option value="beta">测试版</option></select></label>
        </div></section>
        <section className="form-section"><h3>版本范围</h3><div className="form-grid two">
          <label><span>最新版本</span><input value={draft.latestVersion} onChange={(event) => update("latestVersion", event.target.value)} placeholder="1.3.0" /></label>
          <label><span>最低可用版本</span><input value={draft.minSupportedVersion} onChange={(event) => update("minSupportedVersion", event.target.value)} placeholder="1.2.0" /></label>
        </div></section>
        <section className="form-section"><h3>下载入口</h3><div className="form-grid two">
          <label className="span-two"><span>第三方网盘链接</span><div className="input-action"><input type="url" value={draft.downloadPageUrl} onChange={(event) => update("downloadPageUrl", event.target.value)} placeholder="https://..." />{draft.downloadPageUrl && <a href={draft.downloadPageUrl} target="_blank" rel="noreferrer" title="打开链接"><ExternalLink size={17} /></a>}</div></label>
          <label><span>备用链接</span><input type="url" value={draft.backupDownloadPageUrl ?? ""} onChange={(event) => update("backupDownloadPageUrl", event.target.value || null)} placeholder="可选" /></label>
          <label><span>提取码</span><input value={draft.accessCode ?? ""} onChange={(event) => update("accessCode", event.target.value || null)} placeholder="可选" /></label>
          <label className="span-two"><span>安装包 SHA-256</span><input className="mono" value={draft.sha256 ?? ""} onChange={(event) => update("sha256", event.target.value || null)} placeholder="可选，64 位十六进制" /></label>
        </div></section>
        <section className="form-section"><h3>客户端提示</h3><div className="form-grid two">
          <label><span>提示标题</span><input value={draft.title} onChange={(event) => update("title", event.target.value)} /></label>
          <label><span>强升生效时间</span><input type="datetime-local" value={localDateTimeValue(draft.enforceAt)} onChange={(event) => update("enforceAt", event.target.value ? new Date(event.target.value).toISOString() : null)} /></label>
          <label className="span-two"><span>更新说明，每行一项</span><textarea rows={5} value={notes} onChange={(event) => setNotes(event.target.value)} /></label>
        </div></section>
        <section className={`force-panel ${draft.forceUpgrade ? "is-active" : ""}`}>
          <div><ShieldAlert size={19} /><span><strong>强制升级</strong><small>低于最低可用版本的客户端将无法进入主界面</small></span></div>
          <label className="switch"><input type="checkbox" aria-label="启用强制升级" checked={draft.forceUpgrade} onChange={(event) => update("forceUpgrade", event.target.checked)} /><i /></label>
          {draft.forceUpgrade && <label className="danger-confirm"><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /><span>确认阻止低于 v{draft.minSupportedVersion} 的客户端使用</span></label>}
        </section>
        <section className="publish-row"><label><input type="checkbox" checked={draft.enabled} onChange={(event) => update("enabled", event.target.checked)} /><span>启用此策略</span></label><div>{message && <Status tone="success">{message}</Status>}{error && <Status tone="danger">{error}</Status>}<button className="primary-button" type="submit" disabled={busy || !draft.downloadPageUrl || (draft.forceUpgrade && !confirmed)}>{busy ? <RefreshCw className="spin" size={17} /> : <Save size={17} />}{busy ? "保存中" : "保存策略"}</button></div></section>
    </form>
  </section>;
}
